/**
 * INLOGGNINGEN. En sexsiffrig kod i mejlet — inget lösenord, ingen länk.
 *
 * VARFÖR EN KOD OCH INTE EN LÄNK. Till 2026-09-11 mejlade Klartid en länk.
 * Ägaren installerade appen på hemskärmen på sin iPhone, begärde en länk,
 * tryckte på den — och hamnade i Safari. Safari och en installerad webbapp
 * delar inte lagring på iOS, så hemskärmsappen var fortfarande utloggad. En
 * hantverkare som möter det ger upp. Med en kod lämnar han aldrig appen: han
 * läser sex siffror i mejlet och skriver in dem här, och på iPhone föreslår
 * tangentbordet koden direkt ur mejlet (autoComplete="one-time-code").
 *
 * KONTRAKTETS EGEN TVÅSTEGSDIALOG, och den är inte påklistrad här:
 * AuthRequestCodeRequest säger att `name` sätts BARA när kontot skapas första
 * gången — "Är e-posten OKÄND och name saknas svarar servern VALIDATION_FAILED
 * { field: 'name' } — ett konto skapas aldrig utan namn. Är e-posten känd
 * ignoreras name."
 *
 * Skärmen frågar därför ALDRIG efter namnet i första steget. Den skickar
 * adressen, och får den VALIDATION_FAILED { field: 'name' } tillbaka vet den
 * att adressen är okänd och frågar då. En som redan är kund skriver alltså bara
 * sin adress; en ny får en fråga till. Det är också varför rutten inte får
 * svara något annat: skulle den säga "kontot finns inte" vore den en
 * orakelfråga som läcker vilka adresser som är kunder.
 *
 * SVARET SÄGER INTE OM BREVET GICK IVÄG TILL ETT NYTT ELLER BEFINTLIGT KONTO —
 * det är { sent: true } och ingenting mer, och det bär aldrig koden.
 *
 * FEL KOD ÄR UNAUTHENTICATED, samma svar som för en utgången kod och för ett
 * konto som inte finns (worker/src/modules/account/index.ts): servern säger
 * aldrig VILKET. Kontraktets text för den koden ("Du är utloggad …") är
 * skriven för en telefon vars bevis gått ut, inte för den som just skrev fel
 * siffra — så här, och bara här, skriver skärmen sin egen rad: "Fel kod" och
 * hur många försök som är kvar. Nedräkningen är sann eftersom servern och
 * klienten läser samma tal, LOGIN_CODE_MAX_ATTEMPTS i shared/types.ts.
 *
 * ATT BE OM KODEN IGEN ger inom samma tidsfönster SAMMA kod (den är härledd,
 * inte slumpad — login-code.ts). Knappen finns för den vars brev inte kom,
 * inte för att få ett nytt tal, och den väntar en halv minut så att ingen
 * hinner mejlbomba sig själv av otålighet.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTH_REQUEST_CODE, AUTH_VERIFY } from '@delat/routes';
import {
  LOGIN_CODE_DIGITS,
  LOGIN_CODE_MAX_ATTEMPTS,
  LOGIN_CODE_TTL_MINUTES,
} from '@delat/types';
import { enhetsId, glomUtloggningsfel, klienten } from '../api';
import type { Klartidfel } from '../api';
import { granskaAuthVerify, granskaKodSkickad } from '../api/granskare/konto';
import { Falt, Faltholje, Felruta, Knapp, Rubrik, Tomtext, Yta, Yttreram } from '../ui';

type Steg = 'adress' | 'namn' | 'kod';

/** Sekunder innan "Skicka en ny kod" dyker upp. */
const NY_KOD_EFTER_SEKUNDER = 30;

/**
 * Sekunder spärren varar när försöken är slut. Samma tal som serverns
 * LOGIN_RATE_LIMIT-period (worker/wrangler.jsonc: 60 s) — servern säger det
 * själv i RATE_LIMITED { retry_after_ms }, och det värdet används när det
 * finns; talet här är reserven när spärren slog i lokalt.
 */
const SPARR_SEKUNDER = 60;

const MINUTER_I_ORD: Readonly<Record<number, string>> = { 5: 'fem', 10: 'tio', 15: 'femton' };
const minuterIOrd = (n: number): string => MINUTER_I_ORD[n] ?? String(n);

/** Bara siffror, högst LOGIN_CODE_DIGITS. '482 917' klistrat -> '482917'. */
const baraSiffror = (text: string): string =>
  text.replace(/[^0-9]/g, '').slice(0, LOGIN_CODE_DIGITS);

/** '482917' -> '482 917', som i brevet. Bara för visning. */
const medMellanrum = (kod: string): string =>
  kod.length > 3 ? `${kod.slice(0, 3)} ${kod.slice(3)}` : kod;

/**
 * EN klocka för båda nedräkningarna (spärren och "skicka en ny kod"), som
 * tickar en gång i sekunden så länge skärmen finns. Nedräkningarna räknas ur
 * TIDSSTÄMPLAR mot den här klockan i stället för ur egna räknare: två räknare
 * med var sin effekt tävlade om ordningen i samma commit, och spärren
 * nollades i samma ögonblick som den sattes.
 */
const useKlocka = (): number => {
  const [nu, sattNu] = useState(() => Date.now());
  useEffect(() => {
    const id = globalThis.setInterval(() => sattNu(Date.now()), 1000);
    return () => globalThis.clearInterval(id);
  }, []);
  return nu;
};

/** Hela sekunder kvar till en tidsstämpel, aldrig under 0. */
const sekunderTill = (till: number | null, nu: number): number =>
  till === null ? 0 : Math.max(0, Math.ceil((till - nu) / 1000));

export interface LoggaInProps {
  /** Skälet till att han står här, om det fanns ett (utgånget bevis m.m.). */
  readonly skal?: Klartidfel | null;
  /** Ropas när koden gått igenom och bevisen ligger i valvet. */
  readonly vidInloggning: () => void;
}

export const LoggaIn = ({ skal, vidInloggning }: LoggaInProps) => {
  const [steg, sattSteg] = useState<Steg>('adress');
  const [epost, sattEpost] = useState('');
  const [namn, sattNamn] = useState('');
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [skickar, sattSkickar] = useState(false);

  // Skälet (t.ex. "ditt bevis har gått ut") visas tills han bett om en kod.
  // Efter det är varje UNAUTHENTICATED från servern ett svar på HANS försök,
  // och det besvaras i kodsteget — inte som ett skäl över adressfältet.
  const [harBegart, sattHarBegart] = useState(false);

  const skickaKod = async (): Promise<void> => {
    sattSkickar(true);
    sattFel(null);
    const svar = await klienten.anrop(
      AUTH_REQUEST_CODE,
      { data: { email: epost.trim(), ...(steg === 'namn' ? { name: namn.trim() } : {}) } },
      granskaKodSkickad,
    );
    sattSkickar(false);

    if (svar.ok) {
      sattHarBegart(true);
      glomUtloggningsfel();
      sattSteg('kod');
      return;
    }

    // Kontraktets signal om att e-posten är OKÄND: då, och bara då, frågar vi
    // efter namnet.
    const saknarNamn =
      svar.fel.slag === 'api' &&
      svar.fel.code === 'VALIDATION_FAILED' &&
      svar.fel.detalj?.['field'] === 'name';

    if (saknarNamn && steg === 'adress') {
      sattSteg('namn');
      return;
    }
    sattFel(svar.fel);
  };

  if (steg === 'kod') {
    return (
      <Kodsteg
        epost={epost.trim().toLowerCase()}
        vidInloggning={vidInloggning}
        skickaIgen={skickaKod}
        skickar={skickar}
        skickfel={fel}
        annanAdress={() => {
          sattFel(null);
          sattSteg('adress');
        }}
      />
    );
  }

  return (
    <Yttreram>
      <Rubrik
        titel="Logga in"
        under="Vi mejlar en kod. Inget lösenord att komma ihåg."
      />

      {/* Skälet till att han står här: utgånget bevis efter 30 dagar utan nät,
          eller ett bevis servern sagt nej till. Beslut 12 — ingenting är
          förlorat, och det ska sägas rakt ut. */}
      {!harBegart && skal !== undefined && skal !== null && fel === null && (
        <Felruta fel={skal} />
      )}

      <Yta>
        <Falt
          etikett="E-postadress"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          spellCheck={false}
          placeholder="anton@ekror.se"
          varde={epost}
          onAndra={sattEpost}
          disabled={steg === 'namn'}
        />

        {steg === 'namn' && (
          <Falt
            etikett="Ditt namn"
            autoComplete="name"
            placeholder="Anton Ek"
            varde={namn}
            onAndra={sattNamn}
            hjalp="Adressen är ny hos oss, så vi lägger upp ett konto. Namnet är det dina kollegor ser."
          />
        )}
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <div style={{ marginTop: '0.8rem' }}>
        <Knapp
          onClick={() => void skickaKod()}
          disabled={skickar || epost.trim() === '' || (steg === 'namn' && namn.trim() === '')}
        >
          {skickar ? 'Skickar ...' : 'Skicka kod'}
        </Knapp>
      </div>

      <Tomtext>
        Klartid är tidrapportering som gör fakturaunderlag. Inte löneunderlag,
        inte projektstyrning.
      </Tomtext>
    </Yttreram>
  );
};

/* == Kodsteget ============================================================ */

interface Kodstegsprops {
  readonly epost: string;
  readonly vidInloggning: () => void;
  readonly skickaIgen: () => Promise<void>;
  readonly skickar: boolean;
  /** Fel från ett "skicka igen", visas i det här steget. */
  readonly skickfel: Klartidfel | null;
  readonly annanAdress: () => void;
}

const Kodsteg = ({
  epost,
  vidInloggning,
  skickaIgen,
  skickar,
  skickfel,
  annanAdress,
}: Kodstegsprops) => {
  const [kod, sattKod] = useState('');
  const [provar, sattProvar] = useState(false);
  const [felForsok, sattFelForsok] = useState(0);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const nu = useKlocka();
  // Spärrad: försöken är slut (lokalt eller enligt servern). Tidsstämpeln
  // när spärren släpper, eller null.
  const [sparrTill, sattSparrTill] = useState<number | null>(null);
  const sparrKvar = sekunderTill(sparrTill, nu);
  // "Skicka en ny kod" väntar en halv minut per utskick.
  const [nyKodTill, sattNyKodTill] = useState(() => Date.now() + NY_KOD_EFTER_SEKUNDER * 1000);
  const nyKodOm = sekunderTill(nyKodTill, nu);
  const [skickatIgen, sattSkickatIgen] = useState(false);
  const faltet = useRef<HTMLInputElement>(null);
  // Ett försök i taget. Sjätte siffran och knappen kan annars skicka samma kod
  // två gånger inom samma tick, och det andra hade kostat ett av fem försök.
  const pagaende = useRef(false);

  const kvar = Math.max(0, LOGIN_CODE_MAX_ATTEMPTS - felForsok);
  const sparrad = sparrKvar > 0;

  // Spärren har gått ut: servern räknar från noll igen, så det gör vi också.
  useEffect(() => {
    if (sparrTill !== null && nu >= sparrTill) {
      sattSparrTill(null);
      sattFelForsok(0);
      sattFel(null);
    }
  }, [nu, sparrTill]);

  useEffect(() => {
    faltet.current?.focus();
  }, []);

  const prova = useCallback(
    async (siffror: string): Promise<void> => {
      if (pagaende.current || sparrad) return;
      pagaende.current = true;
      sattProvar(true);
      sattFel(null);
      const svar = await klienten.anrop(
        AUTH_VERIFY,
        { data: { email: epost, code: siffror, device_id: enhetsId() } },
        granskaAuthVerify,
      );
      pagaende.current = false;
      sattProvar(false);
      if (svar.ok) {
        // Klienten har redan lagt både kontobeviset och firmornas licenser i
        // valvet (se #absorberaBevis). Här återstår bara att lämna skärmen.
        vidInloggning();
        return;
      }

      // Klienten tolkar varje UNAUTHENTICATED som "du är utloggad" och sparar
      // det som skäl till nästa inloggningsskärm. Här VAR det ett svar på ett
      // försök, inte en utloggning — glöm det, annars står "Du är utloggad"
      // över adressfältet nästa gång han går tillbaka.
      glomUtloggningsfel();
      sattKod('');
      faltet.current?.focus();

      if (svar.fel.slag === 'api' && svar.fel.code === 'RATE_LIMITED') {
        // Servern har låst adressen. Den säger själv hur länge.
        const ms = svar.fel.detalj?.['retry_after_ms'];
        const sekunder =
          typeof ms === 'number' && ms > 0 ? Math.ceil(ms / 1000) : SPARR_SEKUNDER;
        sattFelForsok(LOGIN_CODE_MAX_ATTEMPTS);
        sattSparrTill(Date.now() + sekunder * 1000);
        return;
      }
      if (svar.fel.slag === 'api' && svar.fel.code === 'UNAUTHENTICATED') {
        const forsok = felForsok + 1;
        sattFelForsok(forsok);
        if (forsok >= LOGIN_CODE_MAX_ATTEMPTS) {
          // Servern släpper igenom lika många som vi räknar; nästa försök hade
          // blivit RATE_LIMITED. Lås lokalt lika länge.
          sattSparrTill(Date.now() + SPARR_SEKUNDER * 1000);
        }
        return;
      }
      // Nätet, servern, ett svar med fel form: kontraktets text, ordagrant.
      sattFel(svar.fel);
    },
    [epost, felForsok, sparrad, vidInloggning],
  );

  const andraKod = (text: string): void => {
    const siffror = baraSiffror(text);
    sattKod(siffror);
    // Sjätte siffran skickar själv — det är så iOS-förslaget ur mejlet känns:
    // ett tryck, inloggad. Knappen nedanför finns för den som hellre trycker.
    if (siffror.length === LOGIN_CODE_DIGITS) void prova(siffror);
  };

  const felkod = felForsok > 0 && !sparrad && fel === null;

  return (
    <Yttreram>
      <Rubrik
        titel="Skriv in koden"
        under={`Vi har mejlat en kod till ${epost}. Skriv in den här.`}
      />

      <Yta>
        <Faltholje
          etikett="Koden ur mejlet"
          hjalp={`Sex siffror. Koden gäller i ${minuterIOrd(LOGIN_CODE_TTL_MINUTES)} minuter.`}
          fel={
            sparrad
              ? `För många fel i rad. Vänta ${sparrKvar} s och försök igen.`
              : felkod
                ? `Fel kod. Du har ${kvar} försök kvar.`
                : undefined
          }
        >
          {(id, ogiltigt) => (
            <input
              id={id}
              ref={faltet}
              className="k-kontroll k-kod"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={LOGIN_CODE_DIGITS + 1}
              placeholder="000 000"
              value={medMellanrum(kod)}
              onChange={(e) => andraKod(e.target.value)}
              aria-invalid={ogiltigt}
              disabled={provar || sparrad}
            />
          )}
        </Faltholje>
      </Yta>

      {fel !== null && <Felruta fel={fel} />}
      {skickfel !== null && <Felruta fel={skickfel} />}

      <div style={{ marginTop: '0.8rem' }}>
        <Knapp
          onClick={() => void prova(kod)}
          disabled={provar || sparrad || kod.length !== LOGIN_CODE_DIGITS}
        >
          {provar ? 'Loggar in ...' : 'Logga in'}
        </Knapp>
      </div>

      {nyKodOm === 0 ? (
        <Knapp
          variant="stor-tyst"
          disabled={skickar}
          onClick={() => {
            sattSkickatIgen(false);
            sattNyKodTill(Date.now() + NY_KOD_EFTER_SEKUNDER * 1000);
            void skickaIgen().then(() => sattSkickatIgen(true));
          }}
        >
          {skickar ? 'Skickar ...' : 'Skicka en ny kod'}
        </Knapp>
      ) : (
        <Tomtext>
          {skickatIgen ? 'Ett nytt brev är på väg. ' : 'Kom inget brev? '}
          Du kan be om en ny kod om {nyKodOm} s.
        </Tomtext>
      )}

      <Knapp variant="stor-tyst" onClick={annanAdress}>
        Skicka till en annan adress
      </Knapp>

      <Tomtext>
        Kom inget brev? Titta i skräppostmappen, och kontrollera att adressen är
        rätt stavad.
      </Tomtext>
    </Yttreram>
  );
};
